import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http'; // Import HttpClient and related
import { catchError, tap, map } from 'rxjs/operators';
import { environment } from '../environments/environment';

export interface ChatMessage {
  type: 'user' | 'model' | 'gpt' | 'assistant';
  content: string;
  date: string;
  file?: any[];
  references?: any[];
  imageUrl?: string;  // For DALL-E generated images
}

export interface ChatSession {
  id: string;
  title: string;
  timestamp: string;
  history: ChatMessage[];
  name: string;
  temperature?: number;
  model?: string;
  userId?: string;
  pinned?: boolean;
}
export interface ChatSessions {
  id: string;
  title: string;
  timestamp: string;
  history: ChatMessage[];
  name?: string;
  temperature?: number;
  model?: string;
  userId?: string;
  pinned?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ChatSessionService {
  private sessionsKey = 'chatSessions';
  private pinnedSessionsKey = 'pinnedSessionIds'; // localStorage key for pinned session IDs
  private maxStoredSessions = 50; // Limit stored sessions to prevent quota issues
  private currentSessionSubject: BehaviorSubject<ChatSession | null> =
    new BehaviorSubject<ChatSession | null>(null);
  public currentSession$: Observable<ChatSession | null> =
    this.currentSessionSubject.asObservable();
  private allSessionsSubject: BehaviorSubject<ChatSession[]> =
    new BehaviorSubject<ChatSession[]>([]);
  public allSessions$: Observable<ChatSession[]> =
    this.allSessionsSubject.asObservable();
  // Tracks whether a user's sessions are currently being fetched from the server,
  // so the UI can show a loading state instead of an "empty" message during retrieval.
  private sessionsLoadingSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public sessionsLoading$: Observable<boolean> =
    this.sessionsLoadingSubject.asObservable();
  private apiUrl = environment.apiBaseUrl; // Base API URL
  private isNewChatBlocked: boolean = false; // Block session changes during new chat

  constructor(private http: HttpClient) {
    this.loadAllSessions();
    // Don't automatically create or set any session - wait for user interaction
    // The reference component will handle session restoration based on user intent
  }

  // Get pinned session IDs from localStorage
  private getPinnedSessionIds(): Set<string> {
    try {
      const pinnedJson = localStorage.getItem(this.pinnedSessionsKey);
      if (pinnedJson) {
        return new Set(JSON.parse(pinnedJson));
      }
    } catch (error) {
      console.error('Error loading pinned sessions from localStorage:', error);
    }
    return new Set();
  }

  // Save pinned session IDs to localStorage
  private savePinnedSessionIds(pinnedIds: Set<string>): void {
    try {
      localStorage.setItem(this.pinnedSessionsKey, JSON.stringify([...pinnedIds]));
    } catch (error) {
      console.error('Error saving pinned sessions to localStorage:', error);
    }
  }

  // Apply pinned state from localStorage to sessions
  private applyPinnedState(sessions: ChatSession[]): ChatSession[] {
    const pinnedIds = this.getPinnedSessionIds();
    return sessions.map(session => ({
      ...session,
      pinned: pinnedIds.has(session.id)
    }));
  }

  // Update pinned state in localStorage when a session is pinned/unpinned
  private updatePinnedState(sessionId: string, isPinned: boolean): void {
    const pinnedIds = this.getPinnedSessionIds();
    if (isPinned) {
      pinnedIds.add(sessionId);
    } else {
      pinnedIds.delete(sessionId);
    }
    this.savePinnedSessionIds(pinnedIds);
  }

  private sanitizeTitle(value: string | undefined | null): string {
    return (value || '').replace(/^["'“”]+|["'“”]+$/g, '');
  }

  private normalizeSession(session: ChatSession): ChatSession {
    const nameVal = (session.name || '').trim();
    const titleVal = (session.title || '').trim();
    
    const isNameValid = nameVal.length > 0 && nameVal.toLowerCase() !== 'untitled' && nameVal.toLowerCase() !== 'untitled chat' && nameVal !== 'New Chat';
    const isTitleValid = titleVal.length > 0 && titleVal.toLowerCase() !== 'untitled' && titleVal.toLowerCase() !== 'untitled chat' && titleVal !== 'New Chat';
    
    let preferred = 'New Chat';
    if (isNameValid) {
      preferred = nameVal;
    } else if (isTitleValid) {
      preferred = titleVal;
    } else {
      // Auto-recover title from history if the chat is currently marked with a default name
      const firstUserMsg = session.history?.find(msg => msg.type === 'user');
      if (firstUserMsg && firstUserMsg.content) {
        preferred = this.generateChatTitle(firstUserMsg.content);
        session.name = preferred;
        session.title = preferred;
      } else if (nameVal.length > 0) {
        preferred = nameVal;
      } else if (titleVal.length > 0) {
        preferred = titleVal;
      }
    }
    
    const sanitized = this.sanitizeTitle(preferred);
    return { ...session, name: sanitized, title: sanitized };
  }

  private loadAllSessions(): void {
    try {
      const sessionsJson = sessionStorage.getItem(this.sessionsKey);
      if (sessionsJson) {
        const sessions: ChatSession[] = JSON.parse(sessionsJson);
        const normalized = sessions.map((s) => this.normalizeSession(s));
        // Apply pinned state from localStorage
        const withPinnedState = this.applyPinnedState(normalized);
        this.allSessionsSubject.next(withPinnedState);
      } else {
        this.allSessionsSubject.next([]);
      }
    } catch (error) {
      console.error('Error loading sessions from sessionStorage:', error);
      this.allSessionsSubject.next([]);
    }
  }

  private saveAllSessions(sessions: ChatSession[]): void {
    try {
      const normalized = sessions.map((s) => this.normalizeSession(s));
      
      // Store only metadata for all sessions, without history, to prevent quota issues
      // Keep only recent sessions (sorted by timestamp)
      const sortedSessions = [...normalized].sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      
      // Take only the most recent sessions for storage
      const sessionsToStore = sortedSessions.slice(0, this.maxStoredSessions);
      
      // Store only metadata (no history) to save space
      const sessionMetadata = sessionsToStore.map(s => ({
        id: s.id,
        title: s.title,
        name: s.name,
        timestamp: s.timestamp,
        model: s.model,
        temperature: s.temperature,
        userId: s.userId,
        pinned: s.pinned,
        history: [] // Don't store history - fetch from API when needed
      }));
      
      sessionStorage.setItem(this.sessionsKey, JSON.stringify(sessionMetadata));
      
      // Keep full sessions in memory for the current session, but strip history for non-active ones
      const currentActiveId = this.currentSessionSubject.value?.id;
      const inMemorySessions = normalized.map(s => {
        if (currentActiveId && s.id === currentActiveId) {
          return s; // Keep history for the active session
        }
        return { ...s, history: [] }; // Strip history for all others
      });
      
      this.allSessionsSubject.next(inMemorySessions);
    } catch (error) {
      console.error('Error saving sessions to sessionStorage:', error);
      // If storage still fails, clear it and try with just current session
      try {
        sessionStorage.removeItem(this.sessionsKey);
        console.warn('Cleared sessionStorage due to quota error');
      } catch (e) {
        console.error('Failed to clear sessionStorage:', e);
      }
    }
  }

  // Public method to set all sessions from external source (like API)
  public setAllSessions(sessions: ChatSession[]): void {
    const normalized = sessions.map((s) => this.normalizeSession(s));
    // Apply pinned state from localStorage when setting sessions from API
    const withPinnedState = this.applyPinnedState(normalized);
    this.saveAllSessions(withPinnedState);
  }

  // Check if there's already an empty "New Chat" session
private hasEmptyNewChatSession(): ChatSession | null {
  const sessions = this.allSessionsSubject.value;
  return (
    sessions.find(
      (session) =>
        (session.title === 'New Chat' || session.name === 'New Chat') &&
        session.history.length === 0
    ) || null
  );
}


public createNewSession(
  model: string = 'gpt4-o',
  temperature: number = 0.7
): void {
  // ✅ Check for an existing empty "New Chat" session
  const existingEmptyNewChat = this.hasEmptyNewChatSession();

  if (existingEmptyNewChat) {
    // ✅ Navigate to it, regardless of selected model/temperature
    console.log('✅ Navigating to existing empty "New Chat" session.');
    this.setCurrentSession(existingEmptyNewChat);
    return;
  }

  // ✅ No empty "New Chat" exists — create a new one with selected model
  const newSession: ChatSession = {
    id: this.generateUniqueId(),
    title: 'New Chat',
    name: 'New Chat',
    timestamp: new Date().toISOString(),
    history: [],
    model,
    temperature,
    userId: localStorage.getItem('userId') || 'anonymous',
  };

  const sessions = this.allSessionsSubject.value;
  sessions.unshift(newSession); // add new session to top
  this.saveAllSessions(sessions);
  this.setCurrentSession(newSession);
  // Don't save to API immediately - wait until there's actual conversation

  console.log(`✅ Created new "New Chat" with model: ${model}`);
}

// Create a session only when user starts interacting (sends first message)
public createSessionOnFirstInteraction(
  model: string = 'gpt4-o',
  temperature: number = 0.7
): ChatSession {
  const newSession: ChatSession = {
    id: this.generateUniqueId(),
    title: 'New Chat',
    name: 'New Chat',
    timestamp: new Date().toISOString(),
    history: [],
    model,
    temperature,
    userId: localStorage.getItem('userId') || 'anonymous',
  };

  const sessions = this.allSessionsSubject.value;
  sessions.unshift(newSession); // add new session to top
  this.saveAllSessions(sessions);
  this.setCurrentSession(newSession);

  console.log(`✅ Created session on first interaction with model: ${model}`);
  return newSession;
}

// Clean up empty sessions that were never used
public cleanupEmptySessions(): void {
  const sessions = this.allSessionsSubject.value;
  const sessionsToKeep = sessions.filter(session => {
    // Keep sessions that have actual conversation history
    const hasUserMessage = session.history?.some(msg => msg.type === 'user');
    const hasModelResponse = session.history?.some(msg => msg.type === 'model' || msg.type === 'gpt');
    return hasUserMessage && hasModelResponse;
  });
  
  if (sessionsToKeep.length !== sessions.length) {
    console.log(`Cleaned up ${sessions.length - sessionsToKeep.length} empty sessions`);
    this.saveAllSessions(sessionsToKeep);
  }
}





  // Set the current active session
 public setCurrentSession(session: ChatSession | null): void {
  // Check if we should block this session change
  if (this.isNewChatBlocked && session !== null) {
    console.log('🔥 BLOCKING setCurrentSession - new chat is active, session:', session?.name);
    return; // Don't set the session
  }
  
  console.log('🔥 setCurrentSession called with:', session?.name || 'null');
  this.currentSessionSubject.next(session ? this.normalizeSession(session) : null);
  if (session) {
    localStorage.setItem('currentSessionId', session.id); // ✅ persist it
    console.log('🔥 Saved session ID to localStorage:', session.id);
  } else {
    localStorage.removeItem('currentSessionId'); // Clear when no session
    console.log('🔥 Removed currentSessionId from localStorage');
  }
}


  // Get the current active session
  public getCurrentSession(): ChatSession | null {
    return this.currentSessionSubject.value;
  }

  // Add a message to the current session's history and update storage
  public addMessageToCurrentSession(message: ChatMessage): void {
    const currentSession = this.currentSessionSubject.value;
    if (currentSession) {
      currentSession.history.push(message);
      // Update the title if it's still a default title ('New Chat' or 'Untitled') and the first user message comes in
      const currentTitleLower = (currentSession.title || '').trim().toLowerCase();
      const isDefaultTitle = currentTitleLower === 'new chat' || currentTitleLower === 'untitled' || currentTitleLower === 'untitled chat';
      if (
        isDefaultTitle &&
        message.type === 'user' &&
        currentSession.history.length === 1
      ) {
        const generated = this.generateChatTitle(message.content);
        currentSession.title = generated;
        currentSession.name = generated; // keep name in sync for display
      }
      currentSession.timestamp = new Date().toISOString(); // Update timestamp on new message
      
      // Only save to API after we have both user question and model response
      const hasUserMessage = currentSession.history.some(msg => msg.type === 'user');
      const hasModelResponse = currentSession.history.some(msg => msg.type === 'model' || msg.type === 'gpt');
      
      if (hasUserMessage && hasModelResponse) {
        this.updateSession(currentSession); // This will also save to API via updateSession
      } else {
        // Just update local storage for now
        this.saveAllSessions(this.allSessionsSubject.value);
      }
    }
  }

  // Update an existing session in storage AND API
  public updateSession(updatedSession: ChatSession): void {
    let sessions = this.allSessionsSubject.value;
    const index = sessions.findIndex((s) => s.id === updatedSession.id);
    if (index > -1) {
      const normalizedSession = this.normalizeSession(updatedSession);
      // Preserve the pinned state from the updated session
      normalizedSession.pinned = updatedSession.pinned;
      sessions[index] = normalizedSession;
      this.saveAllSessions(sessions); // Save to sessionStorage

      // Update pinned state in localStorage
      this.updatePinnedState(updatedSession.id, !!updatedSession.pinned);

      // If the updated session is the current one, update the subject as well
      if (this.currentSessionSubject.value?.id === updatedSession.id) {
        this.currentSessionSubject.next(normalizedSession);
      }
      this.saveSessionToApi(updatedSession).subscribe(); // Save updated session to API
    }
  }

  // Delete a session from storage AND API
  public deleteSession(sessionId: string): void {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      console.error('Cannot delete session: User ID not found.');
      return;
    }

    this.deleteSessionFromApi(sessionId, userId).subscribe({
      next: () => {
        console.log('Session deleted successfully from API.');
        let sessions = this.allSessionsSubject.value.filter(
          (s) => s.id !== sessionId
        );
        this.saveAllSessions(sessions); // Update sessionStorage after API success
        // If the deleted session was the current one, clear current session
        if (this.currentSessionSubject.value?.id === sessionId) {
          this.currentSessionSubject.next(null);
          // If no sessions left, create a new one
          if (sessions.length === 0) {
            this.createNewSession();
          } else {
            // Set the first remaining session as current
            this.setCurrentSession(sessions[0]);
          }
        } else if (sessions.length > 0) {
          // If the deleted session was not current, but there are other sessions, select the first one
          this.setCurrentSession(sessions[0]);
        } else {
          // If no sessions left, create a new one
          this.createNewSession();
        }
      },
      error: (err) => {
        console.error('Error deleting session on server:', err);
        // Optionally, alert the user or show a snackbar
      },
    });
  }

  // Generate a unique ID for sessions
  private generateUniqueId(): string {
    return 'session_' + Date.now() + Math.random().toString(36).substr(2, 9);
  }

  private generateChatTitle(content: string): string {
    // Remove surrounding quotes and common question words, then extract key topics
    const noQuotes = (content || "").replace(/^["'“”]+|["'“”]+$/g, "");
    const cleanContent = noQuotes.toLowerCase()
      .replace(/^(what|how|where|when|why|who|which|can|could|would|should|is|are|do|does|did|will|tell me|explain|describe|list|show me|help me|give me)\s+/i, '')
      .replace(/\?+$/, '')
      .trim();

    // Extract key phrases and topics
    const keyWords = cleanContent.split(/\s+/)
      .filter(word => word.length > 3 && !['about', 'with', 'from', 'that', 'this', 'they', 'them', 'their', 'there', 'then', 'than', 'some', 'more', 'most', 'many', 'much', 'very', 'just', 'only', 'also', 'even', 'still', 'well', 'good', 'best', 'better'].includes(word))
      .slice(0, 3);

    if (keyWords.length === 0) {
      return noQuotes.substring(0, 30) + (noQuotes.length > 30 ? '...' : '');
    }

    // Create a title by capitalizing key words
    const title = keyWords
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    // Handle specific patterns for better titles
    if (content.toLowerCase().includes('publisher') || content.toLowerCase().includes('publishing')) {
      return title.includes('Publishing') ? title : title + ' Publishing';
    }
    if (content.toLowerCase().includes('book') || content.toLowerCase().includes('books')) {
      return title.includes('Book') ? title : title + ' Books';
    }
    if (content.toLowerCase().includes('author') || content.toLowerCase().includes('authors')) {
      return title.includes('Author') ? title : title + ' Authors';
    }

    return title.length > 25 ? title.substring(0, 25) + '...' : title;
  }

  // Clear all sessions (local and optionally from API)
  public clearAllSessions(): void {
    const sessions = this.allSessionsSubject.value;
    const userId = localStorage.getItem('userId');
    sessionStorage.removeItem(this.sessionsKey);
    this.allSessionsSubject.next([]);
    this.currentSessionSubject.next(null);
    // Automatically create a new session when clearing all sessions
    this.createNewSession();
    console.log('All sessions cleared, created new chat session');
  }

  // --- API Integration Methods ---

  // Fetches all sessions for a user from the API (metadata only for sidebar)
  public fetchAllSessionsFromApi(userId: string): Observable<ChatSession[]> {
    const apiUrl = `${this.apiUrl}/sessions/user/${userId}?db_name=${environment.dbName}&metadata_only=true`;
    return this.http.get<ChatSession[]>(apiUrl).pipe(
      tap((sessions) => {
        console.log(
          `Successfully fetched session metadata for user ${userId} from API:`,
          sessions
        );
        // Update local sessionStorage and subject with fetched data
        // Apply pinned state from localStorage
        const normalized = sessions.map((s) => this.normalizeSession(s));
        const withPinnedState = this.applyPinnedState(normalized);
        this.saveAllSessions(withPinnedState);
        // Don't automatically set session - let the reference component handle this
        console.log('Session metadata fetched from API, letting component decide which to load');
      }),
      catchError(this.handleError<ChatSession[]>('fetchAllSessionsFromApi', []))
    );
  }

  // Fetches a specific session by ID from the API (full data with history)
  public fetchSessionByIdFromApi(
    sessionId: string,
    userId: string
  ): Observable<ChatSession> {
    const apiUrl = `${this.apiUrl}/sessions/view/${sessionId}?db_name=${environment.dbName}`;
    return this.http.get<any>(apiUrl).pipe(
      tap((response) => {
        // API returns an array, take the first element
        const session: ChatSession = Array.isArray(response) ? response[0] : response;
        if (!session) {
          console.error(`No session found for ID ${sessionId}`);
          return;
        }
        console.log(
          `Successfully fetched full session ${sessionId} from API:`,
          session
        );
        // Ensure the fetched session is integrated into the allSessions list
        const sessions = [...this.allSessionsSubject.value];
        const normalized = this.normalizeSession(session);
        // Preserve pinned state from localStorage
        const pinnedIds = this.getPinnedSessionIds();
        normalized.pinned = pinnedIds.has(normalized.id);
        const index = sessions.findIndex((s) => s.id === normalized.id);
        if (index > -1) {
          sessions[index] = normalized; // Update existing with full data
        } else {
          sessions.unshift(normalized); // Add if new
        }
        // CRITICAL: Set current session FIRST so UI updates immediately
        this.setCurrentSession(normalized);
        
        // Defer saveAllSessions to avoid blocking the rendering thread.
        // saveAllSessions does synchronous sessionStorage.setItem which can
        // block the browser paint, causing only partial chat messages to show.
        setTimeout(() => {
          this.saveAllSessions(sessions);
        }, 0);
      }),
      catchError(this.handleError<ChatSession>('fetchSessionByIdFromApi'))
    );
  }

  // Fetches a specific session's full data (with history) WITHOUT any side effects.
  // Used for exporting chats so we don't change the current/active session.
  public fetchSessionDataById(sessionId: string): Observable<ChatSession | null> {
    const apiUrl = `${this.apiUrl}/sessions/view/${sessionId}?db_name=${environment.dbName}`;
    return this.http.get<any>(apiUrl).pipe(
      // The view endpoint returns an array; normalize to a single session object
      map((response) => {
        const session: ChatSession | null = Array.isArray(response)
          ? (response[0] || null)
          : (response || null);
        return session;
      }),
      // Swallow errors so a single failed session doesn't abort a bulk export
      catchError((error) => {
        console.error(`fetchSessionDataById failed for ${sessionId}:`, error);
        return of(null);
      })
    );
  }

  // Saves (creates/updates) a session to the API
  public saveSessionToApi(session: ChatSession): Observable<any> {
    const userId = localStorage.getItem('userId') || 'anonymous';
    
    const getSafeFileArray = (file: any): any[] => {
      if (!file) return [];
      if (Array.isArray(file)) return file;
      if (typeof file === 'string') {
        try {
          const parsed = JSON.parse(file);
          if (Array.isArray(parsed)) return parsed;
        } catch (e) {}
      }
      return [];
    };

    // Strip large base64 file data from history before saving to backend
    const sanitizedHistory = session.history.map((msg: ChatMessage) => {
      const sanitizedMsg = { ...msg };
      const fileArray = getSafeFileArray(sanitizedMsg.file);
      if (fileArray.length > 0) {
        // Keep only file metadata, remove base64 content
        sanitizedMsg.file = fileArray.map((f: any) => ({
          name: f.name,
          type: f.type,
          size: f.size,
          // Exclude base64, preview, and img to reduce payload size
        }));
      } else {
        sanitizedMsg.file = [];
      }
      return sanitizedMsg;
    });
    
    const payload = {
      id: session.id,
      name: session.name || session.title, // Prefer edited name; fallback to title
      temperature: session.temperature,
      model: session.model,
      timestamp: session.timestamp,
      history: sanitizedHistory, // Use sanitized history without large file data
      userId: userId,
      db_name: environment.dbName
    };
    const headers = { 'Content-Type': 'application/json' };
    return this.http.post(`${this.apiUrl}/sessions`, payload, { headers }).pipe(
      tap(() => console.log('✅ Session successfully saved/updated to API.')),
      catchError(this.handleError<any>('saveSessionToApi'))
    );
  }

  // Deletes a session from the API
  public deleteSessionFromApi(
    sessionId: string,
    userId: string
  ): Observable<any> {
    const apiUrl = `${this.apiUrl}/sessions/delete`;
    const payload = { id: sessionId, userId: userId, db_name: environment.dbName };
    return this.http.post(apiUrl, payload).pipe(
      tap(() => console.log(`✅ Session ${sessionId} deleted from API.`)),
      catchError(this.handleError<any>('deleteSessionFromApi'))
    );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: HttpErrorResponse): Observable<T> => {
      console.error(`${operation} failed:`, error);
      // Let the app continue by returning an empty result or rethrowing for UI
      return throwError(
        () => new Error(`Something went wrong`)
      );
    };
  }

  public fetchUserSessionsAndSet(userIds: string): void {
    console.log(userIds);
    const userId = localStorage.getItem('userId');
    // Fetch metadata only (no history) for sidebar - dramatically reduces payload size
    const apiUrl = `${this.apiUrl}/sessions/user/${userId}?db_name=${environment.dbName}&metadata_only=true`;
    this.sessionsLoadingSubject.next(true);
    this.http.get<ChatSession[]>(apiUrl).subscribe({
      next: (sessions) => {
        console.log(`Fetched session metadata for user ${userId}:`, sessions);
        // Apply pinned state from localStorage
        const normalized = sessions.map((s) => this.normalizeSession(s));
        const withPinnedState = this.applyPinnedState(normalized);
        this.saveAllSessions(withPinnedState);

        const remainingSessions = this.allSessionsSubject.value;
        // Don't automatically set a session - let the reference component handle this
        // based on whether user is starting a new chat or not
        console.log('Found ' + remainingSessions.length + ' sessions (metadata only), waiting for component to decide which to load');
        this.sessionsLoadingSubject.next(false);
      },
      error: (error) => {
        console.error('Error fetching sessions for user ' + userId + ':', error);
        this.sessionsLoadingSubject.next(false);
      },
    });
  }

  // Methods to control new chat blocking
  public blockNewChatSessionChanges(): void {
    console.log('🔥 ACTIVATING session change blocking for new chat');
    this.isNewChatBlocked = true;
  }

  public unblockNewChatSessionChanges(): void {
    console.log('🔥 DEACTIVATING session change blocking');
    this.isNewChatBlocked = false;
  }
}
